import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompany } from 'models/companies'
import { ContactCompanyType } from 'models/contact_companies'
import { DealsPipeline } from 'models/deals_pipeline'
import { useCallback } from 'react'

type CompanyRequiredFields = {
  id: number
  company_type?: ContactCompanyType
  chain?: string
  university_id?: number
  k12district_id?: number
  lodging_id?: number
}

type PrefixMap = Record<string, string> | undefined

/**
 * Pure function - gets the url for the company based on the type
 */
export const getCompanyUrl = (
  company: CompanyRequiredFields | ContactCompany,
  prefixMap: PrefixMap
): string => {
  if (!company || !prefixMap) return '-'

  const { company_type, chain, id, university_id, k12district_id, lodging_id } =
    company

  switch (company_type) {
    case ContactCompanyType.Chain:
      return `/chain/${prefixMap[company_type]}${chain}`
    case ContactCompanyType.University:
      return `/university/${prefixMap[company_type]}${university_id}`
    case ContactCompanyType.K12District:
      return `/k12/${prefixMap[company_type]}${k12district_id}`
    case ContactCompanyType.Lodging:
      return `/lodging/chain/${prefixMap[company_type]}${lodging_id}`
    case ContactCompanyType.UserGenerated:
      return `/company/${prefixMap[company_type]}${id}`
    default:
      return `/company/${prefixMap[ContactCompanyType.UserGenerated]}${id}`
  }
}

/**
 * Hook wrapper - returns a function that gets the company url
 */
export const useCompanyUrl = () => {
  const { preferences } = usePreferences()
  const prefixMap = preferences?.company_type_prefix_map
  return useCallback(
    (company: CompanyRequiredFields | ContactCompany) =>
      getCompanyUrl(company, prefixMap),
    [prefixMap]
  )
}

/**
 * Pure function - gets the url for the company based on v2 params
 */
export const getCompanyV2Url = (
  params: {
    company_type?: string
    chain_id?: number
    university_id?: number
    k12district_id?: number
    lodging_id?: number
    id?: number
  },
  prefixMap: PrefixMap
): string => {
  if (!prefixMap) return '-'
  const {
    company_type,
    chain_id,
    university_id,
    k12district_id,
    lodging_id,
    id,
  } = params

  switch (company_type) {
    case ContactCompanyType.Chain:
      return `/chain/${prefixMap[company_type]}${chain_id}`
    case ContactCompanyType.University:
      return `/university/${prefixMap[company_type]}${university_id}`
    case ContactCompanyType.K12District:
      return `/k12/${prefixMap[company_type]}${k12district_id}`
    case ContactCompanyType.Lodging:
      return `/lodging/chain/${prefixMap[company_type]}${lodging_id}`
    case ContactCompanyType.UserGenerated:
      return `/company/${prefixMap[company_type]}${id}`
    default:
      return `/company/${prefixMap[ContactCompanyType.UserGenerated]}${id}`
  }
}

/**
 * Hook wrapper - returns a function that gets the company v2 url
 */
export const useCompanyV2Url = () => {
  const { preferences } = usePreferences()
  const prefixMap = preferences?.company_type_prefix_map
  return useCallback(
    (params: Parameters<typeof getCompanyV2Url>[0]) =>
      getCompanyV2Url(params, prefixMap),
    [prefixMap]
  )
}

/**
 * Pure function - gets the url for the company from a deal pipeline record
 */
export const getCompanyUrlFromDealPipeline = (
  dealPipelineRecord: DealsPipeline,
  prefixMap: PrefixMap
): string => {
  if (!dealPipelineRecord || !prefixMap) return '-'
  const {
    company_type,
    chain_id,
    contact_company_id,
    university_id,
    k12district_id,
    nursinghome_id,
    lodging_id,
  } = dealPipelineRecord

  switch (company_type) {
    case ContactCompanyType.Chain:
      return `/chain/${prefixMap[company_type]}${chain_id}`
    case ContactCompanyType.University:
      return `/university/${prefixMap[company_type]}${university_id}`
    case ContactCompanyType.K12District:
      return `/k12/${prefixMap[company_type]}${k12district_id}`
    case ContactCompanyType.NursingHome:
      return `/nursinghome/${prefixMap[company_type]}${nursinghome_id}`
    case ContactCompanyType.UserGenerated:
      return `/company/${prefixMap[company_type]}${contact_company_id}`
    case ContactCompanyType.Lodging:
      return `/lodging/chain/${prefixMap[company_type]}${lodging_id}`
    default:
      return `/company/${prefixMap[ContactCompanyType.UserGenerated]}${contact_company_id}`
  }
}

/**
 * Hook wrapper - returns a function that gets the company url from a deal pipeline record
 */
export const useCompanyUrlFromDealPipeline = () => {
  const { preferences } = usePreferences()
  const prefixMap = preferences?.company_type_prefix_map
  return useCallback(
    (dealPipelineRecord: DealsPipeline) =>
      getCompanyUrlFromDealPipeline(dealPipelineRecord, prefixMap),
    [prefixMap]
  )
}
